import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { GenerateActivationCodeSchema, type GenerateActivationCodeInput } from "@/types/activation";
import { activationCodeSourceEnum } from "@/server/db/schema/activation";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, PlusCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export function GenerateCodesDialog({ onCodesGenerated }: { onCodesGenerated?: () => void }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<GenerateActivationCodeInput>({
    resolver: zodResolver(GenerateActivationCodeSchema),
    defaultValues: {
      productId: "",
      quantity: 1,
      assignedEmail: "",
      source: "manual", // Default to manual creation
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // Default to 30 days from now
    },
  });

  const utils = trpc.useUtils();
  const generateCodesMutation = trpc.activation.generateActivationCodes.useMutation({
    onSuccess: (data) => {
      toast.success(t("admin.activation.codes.generateSuccess", { count: data.length }));
      utils.activation.listActivationCodes.invalidate(); // Invalidate codes list
      onCodesGenerated?.();
      setIsOpen(false);
      form.reset();
    },
    onError: (error) => {
      toast.error(t("admin.activation.codes.generateError", { message: error.message }));
    },
  });

  const { data: productsData, isLoading: isLoadingProducts } = trpc.activation.listProducts.useQuery({});

  const onSubmit = (values: GenerateActivationCodeInput) => {
    generateCodesMutation.mutate(values);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t("admin.activation.codes.generateNew")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t("admin.activation.codes.generateCodesTitle")}</DialogTitle>
          <DialogDescription>
            {t("admin.activation.codes.generateCodesDesc")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="productId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.codes.productLabel")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoadingProducts}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder={t("admin.activation.codes.selectProductPlaceholder")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingProducts ? (
                        <SelectItem value="" disabled>
                          {t("common.loading")}
                        </SelectItem>
                      ) : productsData?.items?.length ? (
                        productsData.items
                          .filter(product => product.id !== "")
                          .map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))
                      ) : (
                        <SelectItem value="" disabled>
                          {t("admin.activation.codes.noProductsAvailable")}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.quantityLabel")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        placeholder={t("admin.activation.codes.quantityPlaceholder")}
                        className="h-10"
                        {...field}
                        onChange={e => field.onChange(Number.parseInt(e.target.value, 10))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assignedEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.assignedEmailLabel")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t("admin.activation.codes.assignedEmailPlaceholder")}
                        className="h-10"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.sourceLabel")}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder={t("admin.activation.codes.selectSourcePlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activationCodeSourceEnum.enumValues.map((sourceValue) => (
                          <SelectItem key={sourceValue} value={sourceValue}>
                            {t(`admin.activationCodeSource.${sourceValue}`, sourceValue)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiresAt"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.expiresAtLabel")}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal h-10",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>{t("admin.activation.codes.expiresAtPlaceholder")}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0)) } // Disable past dates
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={generateCodesMutation.isPending}>
                {generateCodesMutation.isPending ? t("common.creating") : t("admin.activation.codes.generateNew")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}